import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { Text } from '@grandvisionhq/elements';
import { useIntl } from '@grandvisionhq/state';
import { FormContext } from '../../Form/Form.js';
import Input from '../../Input/Input.js';
import { Toggletip } from '../../Toggletip/Toggletip.js';
import { isDate, isValidDay, isValidMonth, isValidYear } from '../../Util/validations.js';
import { initializeFields, isEqualState, validateFullDate, updateFields, validateWithRequired, joinValues, focusElementWhenValueReady, getErrorMessageBasedOnValidation, getFieldNames } from './utils.js';

const Date = (_param)=>{
    var { name: name1 , label , subHeading , validations , disabled =false , required =false , initialState: initialState1 , additionalScreenReaderLabel , tooltipText , hideAsteriskSign , fieldLabels , onChange , onBlur , className , invalidDateErrorMessage  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "name",
        "label",
        "subHeading",
        "validations",
        "disabled",
        "required",
        "initialState",
        "additionalScreenReaderLabel",
        "tooltipText",
        "hideAsteriskSign",
        "fieldLabels",
        "onChange",
        "onBlur",
        "className",
        "invalidDateErrorMessage"
    ]);
    var ref11, ref1, ref2, ref3, ref4, ref5;
    const { getLabel  } = useIntl();
    const defaultValidations = React.useMemo(()=>[
            {
                validator: isDate,
                errorMessage: invalidDateErrorMessage
            }, 
        ]
    , [
        invalidDateErrorMessage
    ]);
    const [validationResults, setValidationResults] = React.useState([]);
    const [isFullDateValid, setIsFullDateValid] = React.useState(true);
    var ref6;
    const [state1, setState] = React.useState({
        name: name1,
        fields: {},
        isTouched: !!(initialState1 === null || initialState1 === void 0 ? void 0 : initialState1.isTouched),
        isValid: !!(initialState1 === null || initialState1 === void 0 ? void 0 : initialState1.isValid),
        value: (ref6 = initialState1 === null || initialState1 === void 0 ? void 0 : initialState1.value) !== null && ref6 !== void 0 ? ref6 : '',
        errorMessage: invalidDateErrorMessage
    });
    const formContext = React.useContext(FormContext);
    const monthRef = /*#__PURE__*/ React.createRef();
    const yearRef = /*#__PURE__*/ React.createRef();
    /** Get initial state of the field on mount */ React.useEffect(()=>{
        var /** Pass initial state to the form context */ ref;
        const fields = initializeFields({
            name: name1,
            value: state1.value,
            required
        });
        setState(swcHelpers.objectSpread({}, state1, {
            value: state1.value,
            fields
        }));
        (ref = formContext.onChange) === null || ref === void 0 ? void 0 : ref.call(formContext, swcHelpers.objectSpread({}, state1, {
            value: state1.value
        }));
        onChange === null || onChange === void 0 ? void 0 : onChange(swcHelpers.objectSpread({}, state1, {
            value: state1.value
        }));
    }, []);
    React.useEffect(()=>{
        var ref;
        const initialState = (ref = formContext.getInitialState) === null || ref === void 0 ? void 0 : ref.call(formContext, name1);
        const fields = initializeFields({
            name: name1,
            value: initialState === null || initialState === void 0 ? void 0 : initialState.value,
            isTouched: initialState === null || initialState === void 0 ? void 0 : initialState.isTouched,
            required
        });
        var ref12;
        const newState = swcHelpers.objectSpread({}, state1, {
            fields,
            value: (ref12 = initialState === null || initialState === void 0 ? void 0 : initialState.value) !== null && ref12 !== void 0 ? ref12 : '',
            isTouched: !!(initialState === null || initialState === void 0 ? void 0 : initialState.isTouched),
            isValid: handleValidateFullDate(fields)
        });
        /** Update state only if form context data changed */ if (!isEqualState(state1, newState)) {
            var ref13;
            setState(newState);
            (ref13 = formContext.onChange) === null || ref13 === void 0 ? void 0 : ref13.call(formContext, newState);
            onChange === null || onChange === void 0 ? void 0 : onChange(newState);
        }
    /** Run only on form context data changed */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formContext
    ]);
    const handleValidateFullDate = React.useCallback((fields)=>{
        const allValidations = [
            ...validations || [],
            ...defaultValidations
        ];
        if (!allValidations) {
            return true;
        }
        const validateFullDateResults = allValidations.map(({ validator  })=>validateFullDate(name1, fields, required || false, validator)
        );
        const isValid = validateFullDateResults.indexOf(false) === -1;
        setValidationResults(validateFullDateResults);
        setIsFullDateValid(isValid);
        return isValid;
    }, [
        defaultValidations,
        name1,
        required,
        validations
    ]);
    const handleInputChange = React.useCallback(({ state: inputState , name: inputName , validator , ref  })=>{
        var ref14, /** Update form context with the new state after field value is changes */ ref15;
        /** Get new state fields based on new value */ const fields = updateFields(inputName, state1.fields, {
            value: inputState.value,
            isValid: validateWithRequired(inputState.value, validator, {
                required
            })
        });
        /** Get new state based on new fields and value */ const formState = swcHelpers.objectSpread({}, state1, {
            fields,
            value: joinValues(name1, fields),
            isValid: handleValidateFullDate(fields),
            name: name1
        });
        var ref16;
        const fieldIsValid = (ref16 = (ref14 = fields[inputName]) === null || ref14 === void 0 ? void 0 : ref14.isValid) !== null && ref16 !== void 0 ? ref16 : true;
        /** If ref provided jump to the next field if the current field has a valid value */ ref && fieldIsValid && focusElementWhenValueReady(inputState.value, ref);
        setState(formState);
        (ref15 = formContext.onChange) === null || ref15 === void 0 ? void 0 : ref15.call(formContext, formState);
        onChange === null || onChange === void 0 ? void 0 : onChange(formState);
    }, [
        state1,
        formContext,
        name1,
        required,
        onChange,
        handleValidateFullDate
    ]);
    /** On blur touch current field and the whole field too */ const handleBlur = React.useCallback((name)=>{
        var ref, ref17, ref18;
        var ref19;
        const fields = updateFields(name, state1.fields, {
            value: (ref19 = (ref = state1.fields) === null || ref === void 0 ? void 0 : (ref17 = ref[name]) === null || ref17 === void 0 ? void 0 : ref17.value) !== null && ref19 !== void 0 ? ref19 : '',
            isTouched: true
        });
        /** Touch the entire date state when all fields are touched */ const isAllFieldsTouched = !Object.values(fields !== null && fields !== void 0 ? fields : {}).some(({ isTouched  })=>!isTouched
        );
        const newState = swcHelpers.objectSpread({}, state1, {
            fields,
            isTouched: isAllFieldsTouched
        });
        setState(newState);
        (ref18 = formContext.onBlur) === null || ref18 === void 0 ? void 0 : ref18.call(formContext, newState);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(newState);
    }, [
        onBlur,
        formContext,
        state1
    ]);
    var ref7;
    const isAllFieldsValid = React.useMemo(()=>{
        return !Object.values((ref7 = state1 === null || state1 === void 0 ? void 0 : state1.fields) !== null && ref7 !== void 0 ? ref7 : {}).some(({ isValid  })=>!isValid
        );
    }, [
        state1
    ]);
    const applyClasses = React.useCallback((name)=>{
        var ref;
        const field = (ref = state1.fields) === null || ref === void 0 ? void 0 : ref[name];
        /** Return based class for the input id no field */ if (!field) {
            return 'form-control__input';
        }
        /** Return invalid class if only full date invalid.
       * E.g. 1994-02-31 the full date is invalid, but technically each of the fields are valid.
       */ if (isAllFieldsValid && !isFullDateValid && state1.isTouched === true) {
            return 'form-control__input _is_invalid';
        }
        return classnames('form-control__input', {
            _is_valid: (field === null || field === void 0 ? void 0 : field.value) && (field === null || field === void 0 ? void 0 : field.isValid) && (field === null || field === void 0 ? void 0 : field.isTouched) === true,
            _is_invalid: !(field === null || field === void 0 ? void 0 : field.isValid) && (field === null || field === void 0 ? void 0 : field.isTouched) === true
        });
    }, [
        isAllFieldsValid,
        isFullDateValid,
        state1
    ]);
    const { day , month , year  } = getFieldNames(name1);
    const handleOnDayChange = React.useCallback((state)=>{
        handleInputChange({
            state,
            name: day,
            validator: isValidDay,
            ref: monthRef
        });
    }, [
        day,
        monthRef,
        handleInputChange
    ]);
    const handleOnMonthChange = React.useCallback((state)=>{
        handleInputChange({
            state,
            name: month,
            validator: isValidMonth,
            ref: yearRef
        });
    }, [
        month,
        yearRef,
        handleInputChange
    ]);
    const handleOnYearChange = React.useCallback((state)=>{
        handleInputChange({
            state,
            name: year,
            validator: isValidYear
        });
    }, [
        year,
        handleInputChange
    ]);
    const handleOnDayBlur = React.useCallback(()=>handleBlur(day)
    , [
        day,
        handleBlur
    ]);
    const handleOnMonthBlur = React.useCallback(()=>handleBlur(month)
    , [
        month,
        handleBlur
    ]);
    const handleOnYearBlur = React.useCallback(()=>handleBlur(year)
    , [
        year,
        handleBlur
    ]);
    const showAsterisk = required && !hideAsteriskSign;
    const showTopContainer = !!additionalScreenReaderLabel || showAsterisk || !!tooltipText;
    var ref8, ref9, ref10;
    return /*#__PURE__*/ React.createElement("fieldset", swcHelpers.extends({}, rest, {
        name: name1,
        className: classnames('date-field', className)
    }, additionalScreenReaderLabel && {
        'aria-describedby': 'additionalScreenReaderLabel'
    }), /*#__PURE__*/ React.createElement("div", {
        className: "date-field__title"
    }, label && /*#__PURE__*/ React.createElement("legend", {
        className: "date-field__legend"
    }, label), showTopContainer && /*#__PURE__*/ React.createElement("div", {
        className: "date-field__top-container"
    }, additionalScreenReaderLabel && /*#__PURE__*/ React.createElement("span", {
        className: "date-field__additional-screen-reader-label",
        id: "additionalScreenReaderLabel"
    }, additionalScreenReaderLabel), showAsterisk && /*#__PURE__*/ React.createElement("span", {
        className: "date-field__asterisk"
    }, "*"), tooltipText && /*#__PURE__*/ React.createElement(Toggletip, {
        message: tooltipText,
        className: "date-field__tooltip"
    }))), subHeading && /*#__PURE__*/ React.createElement(Text, {
        color: "text-secondary",
        className: "date-field__subheading"
    }, subHeading), /*#__PURE__*/ React.createElement("div", {
        className: "date-field__container"
    }, /*#__PURE__*/ React.createElement("label", {
        className: "date-field__label date-field__label__day",
        htmlFor: day
    }, (fieldLabels === null || fieldLabels === void 0 ? void 0 : fieldLabels[day]) && fieldLabels[day], /*#__PURE__*/ React.createElement(Input, {
        id: day,
        placeholder: getLabel('common.placeholder.day.abbr'),
        className: applyClasses(day),
        disabled: disabled,
        onChange: handleOnDayChange,
        onBlur: handleOnDayBlur,
        initialValue: (ref8 = state1 === null || state1 === void 0 ? void 0 : (ref11 = state1.fields) === null || ref11 === void 0 ? void 0 : (ref1 = ref11[day]) === null || ref1 === void 0 ? void 0 : ref1.value) !== null && ref8 !== void 0 ? ref8 : '',
        pattern: "^\\d{0,2}$",
        inputMode: "numeric"
    })), /*#__PURE__*/ React.createElement("label", {
        className: "date-field__label date-field__label__month",
        htmlFor: month
    }, (fieldLabels === null || fieldLabels === void 0 ? void 0 : fieldLabels[month]) && fieldLabels[month], /*#__PURE__*/ React.createElement(Input, {
        id: month,
        placeholder: getLabel('common.placeholder.month.abbr'),
        className: applyClasses(month),
        disabled: disabled,
        inputRef: monthRef,
        onChange: handleOnMonthChange,
        onBlur: handleOnMonthBlur,
        initialValue: (ref9 = state1 === null || state1 === void 0 ? void 0 : (ref2 = state1.fields) === null || ref2 === void 0 ? void 0 : (ref3 = ref2[month]) === null || ref3 === void 0 ? void 0 : ref3.value) !== null && ref9 !== void 0 ? ref9 : '',
        pattern: "^\\d{0,2}$",
        inputMode: "numeric"
    })), /*#__PURE__*/ React.createElement("label", {
        className: "date-field__label date-field__label__year",
        htmlFor: year
    }, (fieldLabels === null || fieldLabels === void 0 ? void 0 : fieldLabels[year]) && fieldLabels[year], /*#__PURE__*/ React.createElement(Input, {
        id: year,
        placeholder: getLabel('common.placeholder.year.abbr'),
        className: applyClasses(year),
        disabled: disabled,
        inputRef: yearRef,
        onChange: handleOnYearChange,
        onBlur: handleOnYearBlur,
        initialValue: (ref10 = state1 === null || state1 === void 0 ? void 0 : (ref4 = state1.fields) === null || ref4 === void 0 ? void 0 : (ref5 = ref4[year]) === null || ref5 === void 0 ? void 0 : ref5.value) !== null && ref10 !== void 0 ? ref10 : '',
        pattern: "^\\d{0,4}$",
        inputMode: "numeric"
    }))), state1.isTouched && !state1.isValid && /*#__PURE__*/ React.createElement("span", {
        className: "date-field__error"
    }, getErrorMessageBasedOnValidation(validationResults, [
        ...validations || [],
        ...defaultValidations
    ].map(({ errorMessage  })=>errorMessage
    ))));
};

export { Date as default };
//# sourceMappingURL=Date.js.map
